'use strict';

import Swiper from 'swiper';
import { A11y, Autoplay, Navigation, Pagination, Scrollbar } from 'swiper/modules';

const DIRECT_NAVIGATION_SELECTOR = '.swiper-button-next, .swiper-button-prev';

function setNavigationButtonsNotTabbable(swiper) {
	if (!swiper || !swiper.navigation) {
		return;
	}

	const buttons = [swiper.navigation.prevEl, swiper.navigation.nextEl].filter(Boolean);

	for (const button of buttons) {
		button.setAttribute('tabindex', '-1');
	}
}

function getFocusTargetIndex(swiper, slideIndex) {
	const slidesPerGroup = Number(swiper.params.slidesPerGroup) || 1;

	if (slideIndex >= swiper.activeIndex) {
		return Math.floor(slideIndex / slidesPerGroup) * slidesPerGroup;
	}

	return Math.max(0, Math.floor(slideIndex / slidesPerGroup) * slidesPerGroup);
}

function createFocusInHandler(swiper) {
	return (event) => {
		const slide = event.target.closest('.swiper-slide');

		if (!slide) {
			return;
		}

		const slideIndex = Array.from(swiper.slides || []).indexOf(slide);
		const isVisible = slide.classList.contains('swiper-slide-visible') || slide.classList.contains('swiper-slide-active');

		if (slideIndex === -1 || isVisible) {
			return;
		}

		swiper.slideTo(getFocusTargetIndex(swiper, slideIndex), 0, false);
	};
}

function ensureWrapper(sliderElement) {
	const firstChild = sliderElement.firstElementChild;

	if (firstChild && firstChild.classList.contains('swiper-wrapper')) {
		return firstChild;
	}

	const wrapper = document.createElement('div');
	wrapper.className = 'swiper-wrapper';

	for (const child of Array.from(sliderElement.children)) {
		if (!child.matches(DIRECT_NAVIGATION_SELECTOR)) {
			wrapper.appendChild(child);
		}
	}

	sliderElement.appendChild(wrapper);

	return wrapper;
}

function ensureFooter(sliderElement) {
	let footerEl = sliderElement.nextElementSibling;

	if (!footerEl || !footerEl.classList.contains('slider-footer')) {
		footerEl = document.createElement('div');
		footerEl.className = 'slider-footer';
		sliderElement.after(footerEl);
	}

	return footerEl;
}

function ensureControls(parentEl) {
	let controlsEl = parentEl.querySelector(':scope > .slider-controls');

	if (!controlsEl) {
		controlsEl = document.createElement('div');
		controlsEl.className = 'slider-controls';
		parentEl.appendChild(controlsEl);
	}

	return controlsEl;
}

function ensureNavigation(parentEl) {
	let prevButton = parentEl.querySelector(':scope > .swiper-button-prev');
	let nextButton = parentEl.querySelector(':scope > .swiper-button-next');

	if (!prevButton) {
		prevButton = document.createElement('button');
		prevButton.type = 'button';
		prevButton.className = 'swiper-button-prev slick-prev slider-prev-button';
		prevButton.setAttribute('aria-label', 'Edellinen dia');
		parentEl.appendChild(prevButton);
	}

	if (!nextButton) {
		nextButton = document.createElement('button');
		nextButton.type = 'button';
		nextButton.className = 'swiper-button-next slick-next slider-next-button';
		nextButton.setAttribute('aria-label', 'Seuraava dia');
		parentEl.appendChild(nextButton);
	}

	return { prevButton, nextButton };
}

function ensurePagination(parentEl) {
	let paginationEl = parentEl.querySelector(':scope > .swiper-pagination');

	if (!paginationEl) {
		paginationEl = document.createElement('div');
		paginationEl.className = 'swiper-pagination';
		parentEl.appendChild(paginationEl);
	}

	return paginationEl;
}

function ensureScrollbar(parentEl) {
	let scrollbarEl = parentEl.querySelector(':scope > .swiper-scrollbar');

	if (!scrollbarEl) {
		scrollbarEl = document.createElement('div');
		scrollbarEl.className = 'swiper-scrollbar';
		parentEl.appendChild(scrollbarEl);
	}

	return scrollbarEl;
}

/**
 * Initialise a Swiper slider on the given element.
 *
 * Navigation/pagination/scrollbar get a generated sibling `.slider-footer`
 * (arrows + counter + scrollbar in normal document flow below the slides), which
 * is what every caller currently uses. Alternatively, pass
 * `navigation.nextEl`/`navigation.prevEl` and/or `pagination.el` explicitly to bind
 * pre-existing markup instead — the footer is then skipped for whichever of the two
 * is already supplied. Nothing exercises that path today; it is kept because a
 * caller's own DOM lookup can legitimately come up empty (see below).
 *
 * @param {HTMLElement} sliderElement - The container element to turn into a Swiper.
 * @param {Object} swiperConfig - Swiper configuration options.
 * @returns {Swiper|null} The created Swiper instance, or null if element is invalid.
 */
export function initSlider(sliderElement, swiperConfig) {
	if (!sliderElement || sliderElement.swiper) {
		return sliderElement ? sliderElement.swiper : null;
	}

	const wrapper = ensureWrapper(sliderElement);

	for (const slide of Array.from(wrapper.children)) {
		slide.classList.add('swiper-slide');
	}

	sliderElement.classList.add('swiper', 'slider-initialized');

	const customA11y = swiperConfig && swiperConfig.a11y ? swiperConfig.a11y : {};
	const customEvents = swiperConfig && swiperConfig.on ? swiperConfig.on : {};
	const customModules = swiperConfig && swiperConfig.modules ? swiperConfig.modules : [];
	let focusInHandler;

	const config = {
		...swiperConfig,
		modules: Array.from(new Set([A11y, Navigation, Autoplay, Pagination, Scrollbar, ...customModules])),
		watchSlidesProgress: true,
		a11y: {
			prevSlideMessage: 'Edellinen dia',
			nextSlideMessage: 'Seuraava dia',
			firstSlideMessage: 'Tämä on ensimmäinen dia',
			lastSlideMessage: 'Tämä on viimeinen dia',
			paginationBulletMessage: 'Siirry diaan {{index}}',
			scrollOnFocus: false,
			...customA11y
		},
		on: {
			...customEvents,
			afterInit(swiper) {
				swiper.el.removeAttribute('tabindex');
				setNavigationButtonsNotTabbable(swiper);
				focusInHandler = createFocusInHandler(swiper);
				swiper.el.addEventListener('focusin', focusInHandler, true);

				if (customEvents.afterInit) {
					customEvents.afterInit.call(swiper, swiper);
				}
			},
			beforeDestroy(swiper) {
				if (focusInHandler) {
					swiper.el.removeEventListener('focusin', focusInHandler, true);
				}

				if (customEvents.beforeDestroy) {
					customEvents.beforeDestroy.call(swiper, swiper);
				}
			}
		}
	};

	// Navigation/pagination can point at DOM the caller already has — only build the
	// generated footer for whichever is missing. A caller passing e.g.
	// `navigation: { nextEl: undefined }` because its own DOM lookup came up empty
	// (missing CMS markup) is treated the same as "not provided" below.
	const navigationProvided = config.navigation && typeof config.navigation === 'object' && (config.navigation.nextEl || config.navigation.prevEl);
	const paginationProvided = config.pagination && typeof config.pagination === 'object' && config.pagination.el;

	const wantsNavigation = config.navigation && !navigationProvided;
	const wantsGeneratedControls = wantsNavigation || (config.pagination && !paginationProvided);
	const wantsGeneratedScrollbar = config.scrollbar === true;
	const footerEl = wantsGeneratedControls || wantsGeneratedScrollbar ? ensureFooter(sliderElement) : null;

	if (wantsGeneratedScrollbar) {
		const scrollbarEl = ensureScrollbar(footerEl);
		config.scrollbar = { el: scrollbarEl, draggable: true, hide: false };
	}

	// Group nav arrows and the page counter into one flex row so the layout
	// stays centered and scales with any page count.
	const controlsEl = wantsGeneratedControls ? ensureControls(footerEl) : null;

	if (wantsNavigation) {
		const { prevButton, nextButton } = ensureNavigation(controlsEl);
		const navigationConfig = config.navigation === true ? {} : config.navigation;
		// Generated els are spread in last so they always win over a stale
		// undefined nextEl/prevEl from a caller's failed DOM lookup.
		config.navigation = { ...navigationConfig, nextEl: nextButton, prevEl: prevButton };
	}

	if (config.pagination && !paginationProvided) {
		const paginationEl = ensurePagination(controlsEl);
		const paginationConfig = config.pagination === true ? {} : config.pagination;
		// el is spread in last so it always wins over a stale undefined el
		// from a caller's failed DOM lookup.
		config.pagination = { clickable: true, ...paginationConfig, el: paginationEl };

		// Place the counter between the prev/next arrows.
		const nextButton = controlsEl.querySelector(':scope > .swiper-button-next');

		if (nextButton) {
			controlsEl.insertBefore(paginationEl, nextButton);
		}
	}

	const swiper = new Swiper(sliderElement, config);
	setNavigationButtonsNotTabbable(swiper);
	sliderElement.dataset.state = 'done';

	return swiper;
}

export default initSlider;
